import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Rating,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  Timeline as TimelineIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';

interface Supplier {
  id: string;
  name: string;
  logo: string;
  specialization: string[];
  brands: string[];
  rating: number;
  responseTime: string;
  fulfillmentRate: number;
  totalOrders: number;
  contact: {
    name: string;
    phone: string;
    email: string;
    address: string;
  };
  status: 'active' | 'inactive';
  lastDelivery: Date;
}

interface NewSupplierForm {
  name: string;
  logo: string;
  specialization: string[];
  brands: string[];
  contact: {
    name: string;
    phone: string;
    email: string;
    address: string;
  };
  status: 'active' | 'inactive';
}

export const SupplierNetwork: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [openNewSupplierDialog, setOpenNewSupplierDialog] = useState(false);
  const [newSupplier, setNewSupplier] = useState<NewSupplierForm>({
    name: '',
    logo: '',
    specialization: [],
    brands: [],
    contact: {
      name: '',
      phone: '',
      email: '',
      address: '',
    },
    status: 'active',
  });

  // Mock supplier data for luxury vehicles
  const suppliers: Supplier[] = [
    {
      id: 'SUP001',
      name: 'Ferrari South Africa',
      logo: '/logos/ferrari.png',
      specialization: ['Engine Components', 'Brake Systems', 'Body Parts'],
      brands: ['Ferrari'],
      rating: 4.8,
      responseTime: '2 hours',
      fulfillmentRate: 98,
      totalOrders: 156,
      contact: {
        name: 'James Mitchell',
        phone: '+27 11 234 5678',
        email: 'parts@ferrari.co.za',
        address: 'Melrose Arch, Johannesburg',
      },
      status: 'active',
      lastDelivery: new Date(2024, 2, 15),
    },
    {
      id: 'SUP002',
      name: 'McLaren Johannesburg',
      logo: '/logos/mclaren.png',
      specialization: ['Electronics', 'Aerodynamics', 'Powertrain'],
      brands: ['McLaren'],
      rating: 4.9,
      responseTime: '1 hour',
      fulfillmentRate: 99,
      totalOrders: 89,
      contact: {
        name: 'Sarah Williams',
        phone: '+27 11 345 6789',
        email: 'parts@mclarenjhb.co.za',
        address: 'Sandton, Johannesburg',
      },
      status: 'active',
      lastDelivery: new Date(2024, 2, 18),
    },
    {
      id: 'SUP003',
      name: 'Porsche Centre SA',
      logo: '/logos/porsche.png',
      specialization: ['Performance Parts', 'Suspension', 'Interior'],
      brands: ['Porsche'],
      rating: 4.7,
      responseTime: '3 hours',
      fulfillmentRate: 97,
      totalOrders: 234,
      contact: {
        name: 'Michael van der Merwe',
        phone: '+27 11 456 7890',
        email: 'parts@porsche.co.za',
        address: 'Bryanston, Johannesburg',
      },
      status: 'active',
      lastDelivery: new Date(2024, 2, 16),
    },
    {
      id: 'SUP004',
      name: 'Lamborghini Johannesburg',
      logo: '/logos/lamborghini.png',
      specialization: ['Exhaust Systems', 'Body Kits', 'Engine Parts'],
      brands: ['Lamborghini'],
      rating: 4.6,
      responseTime: '4 hours',
      fulfillmentRate: 96,
      totalOrders: 112,
      contact: {
        name: 'David Peterson',
        phone: '+27 11 567 8901',
        email: 'parts@lamborghini.co.za',
        address: 'Waterfall City, Midrand',
      },
      status: 'active',
      lastDelivery: new Date(2024, 2, 14),
    },
  ];

  const handleViewSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setOpenDialog(true);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleAddNewSupplier = () => {
    setOpenNewSupplierDialog(true);
  };

  const handleNewSupplierSubmit = () => {
    // Here you would typically make an API call to create the supplier
    setOpenNewSupplierDialog(false);
    // Reset form
    setNewSupplier({
      name: '',
      logo: '',
      specialization: [],
      brands: [],
      contact: {
        name: '',
        phone: '',
        email: '',
        address: '',
      },
      status: 'active',
    });
  };

  const handleSaveChanges = () => {
    // Here you would typically make an API call to update the supplier
    setOpenDialog(false);
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Supplier Network
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Total Suppliers
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {suppliers.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active luxury brands
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Average Response Time
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  2.5h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  For urgent requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Fulfillment Rate
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  97.5%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 30 days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Total Orders
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  591
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Across all suppliers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ ml: 1 }}
            onClick={handleAddNewSupplier}
          >
            Add New Supplier
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Supplier</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>Performance</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.map((supplier) => (
                <TableRow key={supplier.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        src={supplier.logo}
                        alt={supplier.name}
                        sx={{ width: 40, height: 40, mr: 2 }}
                      />
                      <Box>
                        <Typography variant="subtitle2">{supplier.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {supplier.brands.join(', ')}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {supplier.specialization.map((spec) => (
                        <Chip
                          key={spec}
                          label={spec}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Rating value={supplier.rating} readOnly size="small" />
                      <Typography variant="body2">
                        {supplier.fulfillmentRate}% Fulfillment
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Response: {supplier.responseTime}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {supplier.contact.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {supplier.contact.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.status}
                      color={supplier.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewSupplier(supplier)}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Edit Supplier Details
          </DialogTitle>
          <DialogContent>
            {selectedSupplier && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={selectedSupplier.logo}
                      alt={selectedSupplier.name}
                      sx={{ width: 60, height: 60, mr: 2 }}
                    />
                    <Box>
                      <Typography variant="h6">{selectedSupplier.name}</Typography>
                      <Typography color="text.secondary">
                        {selectedSupplier.brands.join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    value={selectedSupplier.contact.name}
                    onChange={(e) => {
                      setSelectedSupplier({
                        ...selectedSupplier,
                        contact: {
                          ...selectedSupplier.contact,
                          name: e.target.value,
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={selectedSupplier.contact.phone}
                    onChange={(e) => {
                      setSelectedSupplier({
                        ...selectedSupplier,
                        contact: {
                          ...selectedSupplier.contact,
                          phone: e.target.value,
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={selectedSupplier.contact.email}
                    onChange={(e) => {
                      setSelectedSupplier({
                        ...selectedSupplier,
                        contact: {
                          ...selectedSupplier.contact,
                          email: e.target.value,
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={selectedSupplier.contact.address}
                    onChange={(e) => {
                      setSelectedSupplier({
                        ...selectedSupplier,
                        contact: {
                          ...selectedSupplier.contact,
                          address: e.target.value,
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Rating
                      </Typography>
                      <Rating value={selectedSupplier.rating} readOnly />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Response Time
                      </Typography>
                      <Typography variant="h6">
                        {selectedSupplier.responseTime}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Fulfillment Rate
                      </Typography>
                      <Typography variant="h6">
                        {selectedSupplier.fulfillmentRate}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Orders
                      </Typography>
                      <Typography variant="h6">
                        {selectedSupplier.totalOrders}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={handleSaveChanges}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openNewSupplierDialog}
          onClose={() => setOpenNewSupplierDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Add New Supplier</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  value={newSupplier.name}
                  onChange={(e) =>
                    setNewSupplier({
                      ...newSupplier,
                      name: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Logo URL"
                  value={newSupplier.logo}
                  onChange={(e) =>
                    setNewSupplier({
                      ...newSupplier,
                      logo: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Specialization</InputLabel>
                  <Select
                    multiple
                    value={newSupplier.specialization}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setNewSupplier({
                        ...newSupplier,
                        specialization: value,
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Engine Components">Engine Components</MenuItem>
                    <MenuItem value="Brake Systems">Brake Systems</MenuItem>
                    <MenuItem value="Body Parts">Body Parts</MenuItem>
                    <MenuItem value="Electronics">Electronics</MenuItem>
                    <MenuItem value="Aerodynamics">Aerodynamics</MenuItem>
                    <MenuItem value="Powertrain">Powertrain</MenuItem>
                    <MenuItem value="Interior">Interior</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Brands</InputLabel>
                  <Select
                    multiple
                    value={newSupplier.brands}
                    onChange={(e) => {
                      const value = e.target.value as string[];
                      setNewSupplier({
                        ...newSupplier,
                        brands: value,
                      });
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="Ferrari">Ferrari</MenuItem>
                    <MenuItem value="McLaren">McLaren</MenuItem>
                    <MenuItem value="Porsche">Porsche</MenuItem>
                    <MenuItem value="Lamborghini">Lamborghini</MenuItem>
                    <MenuItem value="Aston Martin">Aston Martin</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Contact Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={newSupplier.contact.name}
                  onChange={(e) =>
                    setNewSupplier({
                      ...newSupplier,
                      contact: {
                        ...newSupplier.contact,
                        name: e.target.value,
                      },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={newSupplier.contact.phone}
                  onChange={(e) =>
                    setNewSupplier({
                      ...newSupplier,
                      contact: {
                        ...newSupplier.contact,
                        phone: e.target.value,
                      },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={newSupplier.contact.email}
                  onChange={(e) =>
                    setNewSupplier({
                      ...newSupplier,
                      contact: {
                        ...newSupplier.contact,
                        email: e.target.value,
                      },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={newSupplier.contact.address}
                  onChange={(e) =>
                    setNewSupplier({
                      ...newSupplier,
                      contact: {
                        ...newSupplier.contact,
                        address: e.target.value,
                      },
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newSupplier.status}
                    label="Status"
                    onChange={(e) =>
                      setNewSupplier({
                        ...newSupplier,
                        status: e.target.value as 'active' | 'inactive',
                      })
                    }
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenNewSupplierDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleNewSupplierSubmit}
              disabled={
                !newSupplier.name ||
                !newSupplier.contact.name ||
                !newSupplier.contact.email ||
                !newSupplier.contact.phone ||
                newSupplier.specialization.length === 0 ||
                newSupplier.brands.length === 0
              }
            >
              Add Supplier
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}; 